import { Component, effect, inject } from '@angular/core';
import { BulkActionStore } from '../../../stores/bulk.state';
import { getState, patchState } from '@ngrx/signals';
import { IndicatorPickListComponent } from '../indicator-pick-list/indicator-pick-list.component';
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { BulkOperationService } from '../../../services/bulk-operation.service';
import { CategoryPickListComponent } from '../category-pick-list/category-pick-list.component';
import { TagPickListComponent } from '../tag-pick-list/tag-pick-list.component';
import { CategoryTreeComponent } from '../../organisms/category-tree/category-tree.component';
import { TagStore } from '../../../stores/tag/tag.state';
import { CategoryStore } from '../../../stores/category/category.state';
import { Category } from '../../../models/category';
import { Indicator } from '../../../models/indicator';
import { IndicatorSetPickListComponent } from '../indicator-set-pick-list/indicator-set-pick-list.component';
import { YearPickListComponent } from '../year-pick-list/year-pick-list.component';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { BulkOverviewComponent } from '../bulk-overview/bulk-overview.component';
import { RoutingService } from '../../../services/routing.service';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';

@Component({
  selector: 'app-bulk-action',
  standalone: true,
  imports: [
    IndicatorPickListComponent,
    DividerModule,
    ButtonModule,
    CategoryPickListComponent,
    TagPickListComponent,
    CategoryTreeComponent,
    IndicatorSetPickListComponent,
    YearPickListComponent,
    BulkOverviewComponent,
  ],
  templateUrl: './bulk-action.component.html',
  styleUrl: './bulk-action.component.scss',
})
export class BulkActionComponent {
  readonly bulkStore = inject(BulkActionStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly categoryStore = inject(CategoryStore);
  readonly dataLayerStore = inject(DataLayerStore);
  readonly dataStore = inject(IndicatorDataStore);
  readonly tagStore = inject(TagStore);

  activeAction = '';
  steps: string[] = [];
  pickIndicators = false;
  pickCategories = false;
  pickTags = false;
  pickSet = false;
  pickCategory = false;

  constructor(
    private bulkOperationService: BulkOperationService,
    private routingService: RoutingService
  ) {
    effect(() => {
      const bulkState = getState(this.bulkStore);
      this.checkForActionChange(bulkState);
    });
  }

  /**
   * Evaluates whether the selected action in the given state differs from the current active action
   * and updates the active action if necessary. If a change occurs, it resets the associated indicators.
   *
   * @param {Object} bulkState - The state object containing the selected action to compare with the active action.
   * @return {void} This method does not return a value.
   */
  private checkForActionChange(bulkState: any): void {
    if (
      bulkState.selectedAction &&
      bulkState.selectedAction !== this.activeAction
    ) {
      this.activeAction = bulkState.selectedAction;
      queueMicrotask(() => {
        this.resetIndicators();
      });
    }
  }

  /**
   * Selects indicators based on the current indicator selection state.
   * If a selection is present in the indicator store, it updates the bulk store
   * with the selected indicators and proceeds to the next step.
   *
   * @return {void} This method does not return a value.
   */
  selectIndicators(): void {
    const indicatorState = getState(this.indicatorStore);
    if (indicatorState.selection && indicatorState.selection.length) {
      patchState(this.bulkStore, {
        indicators: indicatorState.selection,
      });

      this.nextStep();
    }
  }

  /**
   * Selects categories based on the current state of the category store
   * and updates the bulk store state with the selected categories.
   * Triggers the next step in the process if there are selected categories.
   *
   * @return {void} This method does not return a value.
   */
  selectCategories(): void {
    const categoryState = getState(this.categoryStore);
    if (categoryState.selection && categoryState.selection.length) {
      patchState(this.bulkStore, {
        categories: categoryState.selection,
      });

      this.nextStep();
    }
  }

  /**
   * Handles the selection of years and updates the state based on the selection.
   * If there is a valid selection in the data layer state, it patches the bulk store state
   * with the selected data layers and proceeds to the next step in the process.
   *
   * @return {void} This method does not return a value.
   */
  selectYears(): void {
    const dataLayerState = getState(this.dataLayerStore);
    if (dataLayerState.selection && dataLayerState.selection.length) {
      patchState(this.bulkStore, {
        dataLayers: dataLayerState.selection,
      });

      this.nextStep();
    }
  }

  /**
   * Handles the selection of tags from the tag store and updates the bulk store with the selected tags.
   * If there are selected tags, it updates the bulk store's state and proceeds to the next step of the process.
   *
   * @return {void} This method does not return a value.
   */
  selectTags(): void {
    const tagState = getState(this.tagStore);
    if (tagState.selection && tagState.selection.length) {
      patchState(this.bulkStore, {
        tags: tagState.selection,
      });

      this.nextStep();
    }
  }

  /**
   * Updates the state with the selected category.
   *
   * @param {Category} category - The category to be set in the state.
   * @return {void}
   */
  selectCategory(category: Category): void {
    patchState(this.bulkStore, {
      category: category,
    });
  }

  /**
   * Selects the specified indicator set and updates the state with it.
   *
   * @param {Indicator} indicatorSet - The indicator set to be selected and stored in the state.
   * @return {void} This method does not return a value.
   */
  selectSet(indicatorSet: Indicator): void {
    patchState(this.bulkStore, {
      set: indicatorSet,
    });
  }

  /**
   * Advances the current step to the next step in the sequence of steps, if a next step exists.
   * @return {void} This method does not return a value.
   */
  nextStep(): void {
    const steps = this.bulkStore.getSteps();
    const currentStep = this.bulkStore.currentStep();
    const nextStep = steps[steps.indexOf(currentStep) + 1];
    if (nextStep) {
      patchState(this.bulkStore, { currentStep: nextStep });
    }
  }

  /**
   * Handles the action to go back to the previous step in a process.
   * If a previous step is available, updates the current step to the previous step.
   * If no previous step exists, invokes a cancel operation.
   *
   * @return {void} This method does not return a value.
   */
  backStep(): void {
    const prevStep = this.prevStep;
    if (prevStep) {
      console.log('BACK STEP', prevStep);
      patchState(this.bulkStore, { currentStep: prevStep });
    } else {
      this.cancel();
    }
  }

  /**
   * Retrieves the previous step from a sequence of steps.
   *
   * This method determines the current step and returns the step that
   * immediately precedes it in the ordered list of steps. If there are
   * no steps or the current step is the first in the sequence, it returns null.
   *
   * @return {string | null} The previous step in the sequence, or null if no previous step exists.
   */
  get prevStep(): string | null {
    const steps = this.bulkStore.getSteps();
    if (!steps || steps.length === 0) return null;

    const currentStep = this.bulkStore.currentStep();
    return steps[steps.indexOf(currentStep) - 1];
  }

  /**
   * Confirms and executes a bulk operation based on the selected entity type and action.
   * The method retrieves the selected entity type and action from the bulk store,
   * performs a confirmation through the bulk operation service, and then cancels the current process.
   *
   * @return {void} Does not return a value.
   */
  confirmAction(): void {
    const entityType = this.bulkStore.selectedEntityType();
    if (entityType) {
      this.bulkOperationService.confirm(
        entityType,
        this.bulkStore.selectedAction()
      );
      this.cancel();
    }
  }

  /**
   * Cancels the current operation by redirecting to a predefined route
   * and resetting relevant data stores.
   *
   * @return {void} No return value.
   */
  cancel(): void {
    this.routingService.redirectToRoute([
      '/data-backend',
      {
        outlets: {
          overlay: ['sammel-aktionen'],
        },
      },
    ]);
    this.resetStores();
  }

  /**
   * Resets the states of various store instances to their initial states.
   *
   * Specifically:
   * - Resets the bulk store state.
   * - Updates the indicator, category, tag, and data layer stores to have an empty selection array.
   *
   * @return {void} This method does not return a value.
   */
  resetStores(): void {
    this.bulkStore.resetState();
    this.indicatorStore.clearSelection();
    this.indicatorStore.clearFilter();
    this.dataStore.clearSelection();
    this.categoryStore.clearSelection();
    this.categoryStore.clearFilter();
    this.tagStore.clearSelection();
    this.dataLayerStore.clearSelection();
    this.dataLayerStore.clearFilter();
  }

  resetIndicators(): void {
    this.indicatorStore.clearSelection();
    this.indicatorStore.clearFilter();
  }
}
